import React, { useState, useEffect } from 'react';
import { Box, TextField, Chip, Stack, Autocomplete, CircularProgress } from '@mui/material';
import { excuseApi } from '../../commons/http';

const TagInput = ({
  value = '',
  onChange,
  label = 'Tags',
  placeholder = 'Add tag and press Enter',
  maxLength = 200,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch available tags from API
  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      try {
        const res = await excuseApi({
          path: '/tags',
          method: 'GET'
        });
        if (res.status === 200) {
          setAvailableTags(res.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  // Parse comma-separated string to array
  const parseTags = (tagString) => {
    if (!tagString) return [];
    return tagString.split(',').map(tag => tag.trim()).filter(tag => tag);
  };

  // Convert array to comma-separated string
  const tagsToString = (tagsArray) => {
    return tagsArray.join(',');
  };

  const tags = parseTags(value);

  const addTag = (newTag) => {
    if (!newTag || !newTag.trim()) return;

    const trimmedTag = newTag.trim();

    // Check if tag already exists (case-insensitive)
    const tagExists = tags.some(tag => tag.toLowerCase() === trimmedTag.toLowerCase());
    if (tagExists) {
      setInputValue('');
      return;
    }

    // Check max length
    const newTagsString = tags.length > 0 ? `${value},${trimmedTag}` : trimmedTag;
    if (newTagsString.length > maxLength) {
      return;
    }

    const newTags = [...tags, trimmedTag];
    onChange(tagsToString(newTags));
    setInputValue('');
  };

  const handleDelete = (tagToDelete) => {
    const newTags = tags.filter(tag => tag !== tagToDelete);
    onChange(tagsToString(newTags));
  };

  // Filter suggestions: show tags that match input and are not already selected
  const getFilteredSuggestions = () => {
    if (!inputValue) return availableTags.filter(tag => !tags.some(t => t.toLowerCase() === tag.toLowerCase()));

    return availableTags.filter(tag =>
      tag.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.some(t => t.toLowerCase() === tag.toLowerCase())
    );
  };

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={getFilteredSuggestions()}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          // Handle comma input - add tag immediately
          if (newInputValue.includes(',')) {
            const tagToAdd = newInputValue.replace(',', '').trim();
            if (tagToAdd) {
              addTag(tagToAdd);
            }
            setInputValue('');
          } else {
            setInputValue(newInputValue);
          }
        }}
        onChange={(event, newValue) => {
          // When user selects from dropdown
          if (newValue && typeof newValue === 'string') {
            addTag(newValue);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && inputValue) {
            e.preventDefault();
            addTag(inputValue);
          }
        }}
        disabled={disabled}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            size="small"
            helperText={`${value?.length || 0}/${maxLength} characters`}
            onBlur={() => {
              if (inputValue) {
                addTag(inputValue);
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              <Chip
                label={option}
                size="small"
                color="primary"
                variant="outlined"
              />
            </li>
          );
        }}
      />
      {tags.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          {tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onDelete={disabled ? undefined : () => handleDelete(tag)}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default TagInput;
